import { Ban, Copy } from 'lucide-react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { themeColor } from '~/lib/theme-colors';
import { javascript } from '@codemirror/lang-javascript';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';
import CodeMirror from '@uiw/react-codemirror';
import { type FormEvent, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { TextField } from '~/components/form';
import { ListToolbar } from '~/components/layout';
import {
  Button,
  Card,
  CardBody,
  CardSection,
  StatusChip,
  ConfirmModal,
  DataTable,
  RowActionsMenu,
  FormActions,
  Modal,
  Tabs,
  type DataTableColumn,
} from '~/components/ui';
import {
  useCreateSdkKeyMutation,
  useListSdkKeysQuery,
  useRevokeSdkKeyMutation,
} from '~/modules/query/featureFlags';

import { SDK_EXAMPLES } from '~/components/Constants/featureFlagsConstants';
import { Select, errorText, useDetail } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { SdkKey } from '~/components/pages/feature-flags/types';

const copyText = (text: string, done: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success(done))
    .catch(() => toast.error('Copy is unavailable here. Select the text and copy it manually.'));
};

export const SdkKeysTab = ({ reference }: { reference: Reference }) => {
  const detail = useDetail('key');
  const { environments } = reference;
  const keys = useListSdkKeysQuery();
  const [createSdkKey] = useCreateSdkKeyMutation();
  const [revokeSdkKey] = useRevokeSdkKeyMutation();
  const [revoking, setRevoking] = useState<SdkKey | null>(null);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [example, setExample] = useState<string>(SDK_EXAMPLES[0].id);
  // A library color cannot read classes, so the caret comes from the token (DESIGN.md §3.6).
  // Built at render: the token is unreadable at module load, before the stylesheet applies.
  const codeTheme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );

  const selectedEnv =
    environments.find((e) => e.key === environment)?.key ?? environments[0]?.key ?? '';
  const envName = (key: string) => environments.find((e) => e.key === key)?.name ?? key;
  const closeForm = () => {
    setName('');
    setToken(null);
    detail.close();
  };

  const generate = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await createSdkKey({ name: name.trim(), environment: selectedEnv }).unwrap();
      setToken(res.token);
    } catch (err) {
      toast.error(errorText(err, 'The SDK key could not be generated. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = (key: SdkKey) => {
    revokeSdkKey(key.id)
      .unwrap()
      .then(() => toast.success('SDK key revoked'))
      .catch((err) => toast.error(errorText(err, 'The SDK key could not be revoked. Try again.')));
  };

  const columns: DataTableColumn<SdkKey>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (k) => <span className="font-medium text-gray-800 dark:text-white/90">{k.name}</span>,
    },
    { id: 'environment', header: 'Environment', cell: (k) => envName(k.environment) },
    {
      id: 'prefix',
      header: 'Key',
      cell: (k) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{k.prefix}…</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (k) =>
        k.revokedAt ? (
          <StatusChip label="Revoked" tone="neutral" dot />
        ) : (
          <StatusChip label="Active" tone="success" dot />
        ),
    },
    { id: 'created', header: 'Created', cell: (k) => <LocalTime value={k.createdAt} /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (k: SdkKey) => (
        <RowActionsMenu
          testId={`feature-flags-sdk-keys-actions-${k.id}`}
          items={[
            {
              label: 'Revoke',
              testId: 'revoke',
              destructive: true,
              disabled: !!k.revokedAt,
              icon: <Ban className="size-4" aria-hidden="true" />,
              onClick: () => setRevoking(k),
            },
          ]}
        />
      ),
    },
  ];

  const active = SDK_EXAMPLES.find((x) => x.id === example) ?? SDK_EXAMPLES[0];
  // Only the Node example has a CodeMirror language installed; PHP and CFML show as plain text.
  const codeExtensions = useMemo(
    () => (active.id === 'node' ? [javascript({ typescript: true })] : []),
    [active.id],
  );

  return (
    <>
      <Card>
        <ListToolbar
          title="SDK keys"
          description="Connect your apps and manage their environment credentials. Generate a separate key for each app and environment so you can revoke one without affecting the others."
        />
        <CardBody className="p-0">
          <DataTable<SdkKey>
            framed={false}
            testId="feature-flags-sdk-keys-table"
            loading={!keys.data && !keys.error}
            refreshing={keys.isFetching && !!keys.data}
            error={
              keys.error && !keys.data
                ? {
                    what: 'the SDK keys',
                    message: apiErrorMessage(keys.error as ApiErrorInput),
                    onRetry: keys.refetch,
                  }
                : undefined
            }
            emptyMessage="No SDK keys yet."
            emptyDescription={
              environments.length === 0
                ? 'Add an environment on the Environments page before generating a key.'
                : 'Generate a key, choose its environment, and give it to your app.'
            }
            data={keys.data ?? []}
            rowKey={(k) => k.id}
            columns={columns}
          />
        </CardBody>
        <CardBody className="space-y-6 border-t border-gray-200 dark:border-gray-800">
          <CardSection title="Connect an app">
            <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-500 dark:text-gray-400">
              <li>Generate a key for the app's environment and copy it.</li>
              <li>Store the key in the app's backend configuration.</li>
              <li>
                Initialize the Node, PHP or CFML SDK with this Toolbox's base URL and the key.
              </li>
            </ol>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Direct API calls use{' '}
              <code className="font-mono">Authorization: Bearer &lt;SDK key&gt;</code>. Keys stay on
              the backend; browsers receive evaluated feature values.
            </p>
          </CardSection>

          <CardSection
            title="SDK examples"
            description="Set FLAGS_URL to this Toolbox's origin and FLAGS_SDK_KEY to the key you generated. Replace the sample product, flag and user values with your own."
          >
            <div className="space-y-4">
              <Tabs
                variant="pill"
                ariaLabel="SDK language"
                idPrefix="feature-flags-sdk-example"
                activeId={active.id}
                onChange={setExample}
                tabs={SDK_EXAMPLES.map((x) => ({ id: x.id, label: x.label }))}
              />
              <div
                role="tabpanel"
                id={`feature-flags-sdk-example-panel-${active.id}`}
                aria-labelledby={`feature-flags-sdk-example-tab-${active.id}`}
                className="space-y-3"
              >
                <p className="text-sm text-gray-500 dark:text-gray-400">{active.setup}</p>
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-2 dark:border-gray-800">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {active.filename}
                    </span>
                    <Button
                      variant="outline"
                      startIcon={<Copy className="size-4" aria-hidden="true" />}
                      onClick={() => copyText(active.code, 'Code copied')}
                      testId="feature-flags-sdk-copy-code"
                    >
                      Copy code
                    </Button>
                  </div>
                  <CodeMirror
                    aria-label={`${active.label} example`}
                    value={active.code}
                    maxHeight="70vh"
                    theme={codeTheme}
                    extensions={codeExtensions}
                    editable={false}
                    basicSetup={{
                      foldGutter: false,
                      highlightActiveLine: false,
                      lineNumbers: true,
                    }}
                  />
                </div>
              </div>
            </div>
          </CardSection>
        </CardBody>
      </Card>

      <Modal
        isOpen={detail.isNew}
        onClose={closeForm}
        busy={busy}
        title={token ? 'Your SDK key is ready' : 'Generate SDK key'}
        testId="feature-flags-sdk-key-form"
        footer={
          token ? (
            <>
              <Button
                variant="outline"
                startIcon={<Copy className="size-4" aria-hidden="true" />}
                onClick={() => copyText(token, 'Key copied')}
                testId="feature-flags-sdk-key-copy"
              >
                Copy key
              </Button>
              <Button onClick={closeForm} testId="feature-flags-sdk-key-done">
                Done
              </Button>
            </>
          ) : (
            <FormActions
              form="feature-flags-sdk-key-form-body"
              discardLabel="Cancel"
              onDiscard={closeForm}
              saveLabel="Generate key"
              busyLabel="Generating…"
              busy={busy}
              disabled={!name.trim() || !selectedEnv}
              disabledReason={!selectedEnv ? 'Add an environment first' : 'Enter a key name'}
              saveTestId="feature-flags-sdk-key-generate"
            />
          )
        }
      >
        {token ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Copy it now. The full key is shown only here and cannot be retrieved later. Store it
              on your app's backend; it decides which environment the app receives.
            </p>
            <TextField
              id="sdk-key-token"
              testId="sdk-key-token"
              label="SDK key"
              code
              readOnly
              value={token}
              onChange={() => undefined}
            />
          </div>
        ) : (
          <form id="feature-flags-sdk-key-form-body" onSubmit={generate} className="space-y-5">
            <TextField
              id="sdk-key-name"
              testId="sdk-key-name"
              label="Key name"
              required
              value={name}
              onChange={setName}
              placeholder="Office production backend"
            />
            <Select
              id="sdk-key-environment"
              label="Environment"
              required
              value={selectedEnv}
              options={environments.map((e) => ({ value: e.key, label: e.name }))}
              onChange={setEnvironment}
              helperText="Each key serves exactly one environment."
            />
          </form>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!revoking}
        title="Revoke SDK key"
        message={`Revoke '${revoking?.name}'?`}
        confirmLabel="Revoke key"
        variant="danger"
        dangerNote="Apps using this key can no longer connect. Generate and deploy a replacement first if needed."
        testId="feature-flags-sdk-keys-revoke"
        onClose={() => setRevoking(null)}
        onConfirm={() => {
          if (revoking) revoke(revoking);
          setRevoking(null);
        }}
      />
    </>
  );
};

export default SdkKeysTab;
